import {useRef, useCallback, useMemo} from 'react';
import {getWatchedPercentage} from '../utils/getWatchedPercentage';
import {trackEvent} from '@src/Tracking/trackEvent';
import {TraceTokEvents} from '@src/Tracking/catalog';

interface UseVerticalVideoPlayerHandlersProps {
  getContentProperties: () => any;
  refCurrentDuration: React.MutableRefObject<number>;
  watchTime: number;
  setWatchTime: React.Dispatch<React.SetStateAction<number>>;
  lastTimeRef: React.MutableRefObject<number>;
  news: any;
}

export function useVerticalVideoPlayerTracking({
  getContentProperties,
  refCurrentDuration,
  watchTime,
  setWatchTime,
  lastTimeRef,
  news,
}: UseVerticalVideoPlayerHandlersProps) {
  const watchedPercentage = useMemo(
    () => getWatchedPercentage(watchTime, refCurrentDuration.current),
    [watchTime, refCurrentDuration.current],
  );

  // Emit through the module bridge (trackEvent), NOT the useTracking() hook.
  // This component is lazy-loaded into its own chunk where the React
  // TrackingContext is a duplicated instance with no provider above it, so the
  // hook's emit resolves to a silent noop. trackEvent reaches the real pipeline
  // the provider registers globally.
  //
  // content_started + watch_progress are driven off `timeupdate`, the one event
  // the browser fires continuously while the video actually plays. The previous
  // version hung content_started off the play event (which we could not observe
  // reliably) and emitted watch_progress from inside a setState updater (a side
  // effect React does not guarantee to run). Both are now plain, deterministic
  // ref logic here.

  const isEndingRef = useRef(false);
  const hasStartedRef = useRef(false); // emitted content_started for current content
  const startedIdRef = useRef<string | null>(null); // which content id we started
  const wasPausedRef = useRef(false); // a genuine pause happened (=> next play is a resume)
  const watchTimeRef = useRef(0); // accumulated real watch seconds
  const lastHeartbeatRef = useRef<number>(0);
  const completedRef = useRef(false); // content_completed already fired for this content

  const emitStarted = useCallback(
    (id: string | null, cp: any) => {
      hasStartedRef.current = true;
      startedIdRef.current = id;
      isEndingRef.current = false;
      wasPausedRef.current = false;
      watchTimeRef.current = 0;
      lastHeartbeatRef.current = 0;
      completedRef.current = false;
      // P1: content_started
      trackEvent(TraceTokEvents.ContentStarted, {
        content_id: cp.id,
        content_type: cp.typename,
        player_type: 'vertical',
        start_threshold: 0,
        is_flux: false,
      });
      setWatchTime(0);
    },
    [setWatchTime],
  );

  const trackEnd = useCallback(() => {
    if (!news) return;
    isEndingRef.current = true;
    // content_completed normally fires at the 95% milestone in onTimeUpdate.
    // This is the fallback for videos that reach the end without a timeupdate
    // tick landing at/after 95% (e.g. very short clips). Skip if already fired.
    if (completedRef.current) return;
    completedRef.current = true;
    const cp = getContentProperties() || {};
    trackEvent(TraceTokEvents.ContentCompleted, {
      content_id: cp.id,
      content_type: cp.typename,
      completion_threshold: watchedPercentage,
      watch_sec: Math.round(watchTimeRef.current),
    });
  }, [news, getContentProperties, watchedPercentage]);

  const onTimeUpdate = useCallback(
    (seconds: number) => {
      const delta = seconds - lastTimeRef.current;
      lastTimeRef.current = seconds;
      // Ignore seeks, paused gaps, and the initial jump.
      if (!(delta > 0 && delta < 2)) return;

      const cp = getContentProperties() || {};
      const id = (cp.id ?? null) as string | null;

      // First real progression on this content => content_started. This is the
      // reliable trigger: timeupdate only fires while the video is truly playing.
      if (!hasStartedRef.current || startedIdRef.current !== id) {
        emitStarted(id, cp);
      }

      watchTimeRef.current += delta;
      setWatchTime(watchTimeRef.current);

      // P1: watch_progress — a 60s heartbeat.
      if (
        Math.floor(watchTimeRef.current / 60) >
        Math.floor(lastHeartbeatRef.current / 60)
      ) {
        lastHeartbeatRef.current = watchTimeRef.current;
        trackEvent(TraceTokEvents.WatchProgress, {
          content_id: cp.id,
          content_type: cp.typename,
          progression: refCurrentDuration.current
            ? watchTimeRef.current / refCurrentDuration.current
            : 0,
          seconds_watched: watchTimeRef.current,
          heartbeat_time: 60,
        });
      }

      // P1: content_completed at the 95% completion point (by playback
      // position), once per content. Firing from timeupdate means it counts
      // even if the user swipes away just before the very end. watch_sec carries
      // the real seconds watched.
      if (
        !completedRef.current &&
        refCurrentDuration.current > 0 &&
        (seconds / refCurrentDuration.current) * 100 >= 95
      ) {
        completedRef.current = true;
        trackEvent(TraceTokEvents.ContentCompleted, {
          content_id: cp.id,
          content_type: cp.typename,
          completion_threshold: Math.round(
            (seconds / refCurrentDuration.current) * 100,
          ),
          watch_sec: Math.round(watchTimeRef.current),
        });
      }
    },
    [lastTimeRef, setWatchTime, getContentProperties, refCurrentDuration, emitStarted],
  );

  const onPlayEnd = useCallback(() => {
    trackEnd();
    // Reset so a replay (or the next item reusing this component) starts clean.
    hasStartedRef.current = false;
    startedIdRef.current = null;
    wasPausedRef.current = false;
    watchTimeRef.current = 0;
    lastHeartbeatRef.current = 0;
    completedRef.current = false;
    setWatchTime(0);
  }, [trackEnd, setWatchTime]);

  const isAtEnd = (
    event?: React.SyntheticEvent<HTMLVideoElement> | Event | undefined,
  ) => {
    const video =
      event && 'target' in event
        ? (event.target as HTMLVideoElement)
        : undefined;
    if (!video) return false;
    return video.currentTime >= video.duration - 0.5;
  };

  const onVideoPlay = useCallback(
    (_isPaused: boolean, event?: React.SyntheticEvent<HTMLVideoElement>) => {
      const video = event?.currentTarget;
      // The layout calls this on BOTH play and pause; on a pause the element is
      // already paused, so bail and let onVideoPause handle it.
      if (video?.paused) return;
      // content_started is emitted from timeupdate. Only emit a resume here, and
      // only after a genuine pause, so the initial play does not double-count.
      if (!hasStartedRef.current) return;
      if (!wasPausedRef.current) return;
      if (isEndingRef.current) {
        isEndingRef.current = false;
        return;
      }
      if (isAtEnd(event)) return;
      wasPausedRef.current = false;
      const cp = getContentProperties() || {};
      // P3: content_resumed
      trackEvent(TraceTokEvents.ContentResumed, {
        content_id: cp.id,
        platform: 'web',
      });
    },
    [getContentProperties],
  );

  const onVideoPause = useCallback(
    (event?: React.SyntheticEvent<HTMLVideoElement>) => {
      if (!hasStartedRef.current) return;
      if (isEndingRef.current) {
        isEndingRef.current = false;
        return;
      }
      if (isAtEnd(event)) return;
      wasPausedRef.current = true;
      const cp = getContentProperties() || {};
      // P3: content_paused
      trackEvent(TraceTokEvents.ContentPaused, {
        content_id: cp.id,
        seconds_elapsed_bucket: Math.floor(watchTimeRef.current),
      });
    },
    [getContentProperties],
  );

  return {
    onTimeUpdate,
    onPlayEnd,
    onVideoPlay,
    onVideoPause,
    watchedPercentage,
    trackEnd,
  };
}
